import React, { ChangeEventHandler, CSSProperties } from "react";

import { FormControl, FormHelperText, Select as SelectMI, MenuItem, Tooltip } from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import { InfoOutlined as InfoOutlinedIcon } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import classnames from "classnames";
import { FiInfo, FiChevronDown } from "react-icons/fi";

import useStyles from "./styles";
import { OutlinedSelectProps } from "@mui/material";
import { BaseSelectProps } from "@mui/material";

type SelectProps = OutlinedSelectProps & BaseSelectProps;

export type Props = SelectProps & {
  name?: string;
  label?: string;
  error?: boolean;
  errorMessage?: string;
  errorResponse?: Error;
  defaultValue?: unknown;
  value?: string | ReadonlyArray<string> | number | Array<number> | undefined;
  children?: any;
  register?: any;
  style?: CSSProperties;
  labelStyle?: CSSProperties;
  dataCy?: string;
  bordercolor?: string;
  infoMessage?: React.ReactNode | string;
  emptyPlaceholder?: string;
};

const FormControlCustomized = withStyles(() => ({
  root: {
    width: "auto",
    minWidth: "130px",
    display: "flex",
    justifyContent: "flex-end",
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: (props: { bordercolor?: string; disabled?: boolean }) =>
        !props?.disabled ? props?.bordercolor : "#C4C4C4",
    },
    "&:active .MuiOutlinedInput-notchedOutline": {
      borderColor: (props: { bordercolor?: string; disabled?: boolean }) =>
        !props?.disabled ? props?.bordercolor : "#C4C4C4",
    },
    "&:focus .MuiOutlinedInput-notchedOutline": {
      backgroundColor: "transparent",
      borderColor: (props: { bordercolor?: string; disabled?: boolean }) =>
        !props?.disabled ? props?.bordercolor : "#C4C4C4",
    },
    "& .MuiSelect-select:focus": {
      backgroundColor: "transparent !important",
      borderColor: (props: { bordercolor?: string; disabled?: boolean }) =>
        !props?.disabled ? props?.bordercolor : "#C4C4C4",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: (props: { bordercolor?: string; disabled?: boolean }) =>
        !props?.disabled ? props?.bordercolor : "#C4C4C4",
    },
  },
}))(FormControl);

const SelectIcon = styled(FiChevronDown)(() => ({
  marginRight: "5px",
}));

const Select = (props: Props) => {
  const {
    name,
    label,
    labelStyle,
    errorMessage,
    disabled,
    error,
    children,
    defaultValue,
    value,
    className,
    onChange,
    register,
    placeholder,
    displayEmpty = true,
    dataCy,
    required,
    infoMessage,
    ...rest
  } = props;

  const classes = useStyles({ disabled });

  const renderInfoTooltip = () => {
    if (!infoMessage) return null;

    if (typeof infoMessage === "string") {
      if (infoMessage.trim()?.length) {
        return (
          <Tooltip title={infoMessage} placement="top">
            <div className={classes.tooltip}>
              <FiInfo />
            </div>
          </Tooltip>
        );
      }
    }

    return infoMessage;
  };

  const renderRequiredLabel = () => {
    if (required) {
      return <span className={classes.required}>{"*"}</span>;
    }

    return null;
  };

  const renderLabel = () => {
    if (label) {
      return (
        <span className={classes.label} style={labelStyle}>
          {label}
          {renderRequiredLabel()}
          {renderInfoTooltip()}
        </span>
      );
    }

    return null;
  };

  const renderError = () => {
    if (error && errorMessage) {
      return (
        <FormHelperText className={classes.formHelper} data-cy="errors">
          <InfoOutlinedIcon htmlColor="#D92D20" className={classes.iconError} />
          {errorMessage}
        </FormHelperText>
      );
    }
  };

  const renderPlaceholder = () => {
    if (placeholder) {
      return (
        <MenuItem className={classes.menuItemPlaceholder} data-cy="placeholder" disabled value="">
          <span className={classnames(classes.text, classes.placeholder)}>{placeholder}</span>
        </MenuItem>
      );
    }

    return null;
  };

  return (
    <FormControlCustomized
      disabled={disabled}
      bordercolor={error ? "#D92D20" : "#308CDB"}
      fullWidth
      data-cy={`form-control-${dataCy}`}
      variant="outlined"
    >
      {renderLabel()}
      <SelectMI
        tabIndex={0}
        fullWidth
        data-cy={dataCy}
        onChange={onChange}
        name={name}
        defaultValue={defaultValue || ""}
        value={value || ""}
        error={error}
        ref={register}
        disabled={disabled}
        SelectDisplayProps={{ tabIndex: 0 }}
        MenuProps={{
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
          transformOrigin: { horizontal: "center", vertical: "top" },
          classes: {
            paper: classes.menuItem,
          },
          PaperProps: {
            style: {
              borderRadius: "8px",
            },
          },
        }}
        IconComponent={SelectIcon}
        className={classnames(classes.select, classes.text, className)}
        displayEmpty={displayEmpty}
        {...rest}
      >
        {renderPlaceholder()}
        {children}
      </SelectMI>
      {renderError()}
    </FormControlCustomized>
  );
};

export default Select;
